import { Outlet } from "react-router-dom";
import { useSelector } from "react-redux";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import styles from "@/components/Home/Home.module.css";
import { usePreviousLocationTracker } from "../../../utils/ReferrerTracker";
import { selectDisplayErrorSnackBar } from "../../../features/userSlice";
import { handleTracingUserClick } from "../../../utils/trackingHandler";
import { setNavigator } from "../../../utils/navigation";
import { getCurrentVersion } from "../../../version";
import NewVersionPopup from "../../../NewVersionPopup";
import { DrawerScreen } from "../../../pages/Drawer/DrawerScreen";
import ErrorMessageScreen from "../../../pages/ErrorMessage/ErrorMessageScreen";
import BreadCrumbsScreen from "../../../pages/BreadCrumbs/BreadCrumbsScreen";
import { SharedStateProvider } from "../../../components/SharedStateProvider";
import RouteTracker from "../../../components/RouteTracker/RouteTracker";
import { useDeviceDetection } from "../../../customHooks/useDeviceDetection";
import { LicenseInfo } from "@mui/x-license";
import { spacing } from "../../../assets/styles/tokens";

const WorkspaceLayout = () => {
  const navigate = useNavigate();
  const { deviceType } = useDeviceDetection();
  const isDesktop = deviceType === "desktop";

  usePreviousLocationTracker();

  const messageForSnackBar = useSelector(selectDisplayErrorSnackBar);
  LicenseInfo.setLicenseKey(import.meta.env.VITE_MUI_LICENSE_KEY as string);
  const [showUpdatePrompt, setShowUpdatePrompt] = useState(false);
  const [currentVersion, setCurrentVersion] = useState<string | null>(null);
  const [remindMeCount, setRemindMeCount] = useState(0);
  const [isCancelDisabled, setIsCancelDisabled] = useState(false);

  useEffect(() => {
    document.addEventListener("click", handleTracingUserClick);
    return () => {
      document.removeEventListener("click", handleTracingUserClick);
    };
  }, []);

  useEffect(() => {
    setNavigator(navigate);
  }, [navigate]);

  const versionFileUrl = `/meta.json?timeStamp=${Date.now()}`;

  useEffect(() => {
    const fetchVersion = async () => {
      try {
        const version = await getCurrentVersion();
        setCurrentVersion(version);
      } catch (error) {
        console.error("Failed to fetch version:", error);
      }
    };
    fetchVersion();
  }, []);

  useEffect(() => {
    const checkVersion = async () => {
      try {
        const res = await fetch(versionFileUrl, {
          cache: "no-store",
          headers: {
            "Cache-Control": "no-cache",
          },
        });
        const data = await res.json();
        if (data.version !== currentVersion) {
          setShowUpdatePrompt(true);
        }
      } catch (err) {
        console.error("Version check failed:", err);
      }
    };
    const interval = setInterval(checkVersion, 3600000);
    return () => clearInterval(interval);
    // eslint-disable-next-line
  }, [currentVersion, remindMeCount]);

  const handleRefresh = () => {
    window.location.reload();
  };

  const handleCancel = () => {
    const newCount = remindMeCount + 1;
    if (newCount >= 5) {
      setIsCancelDisabled(true);
    }
    setShowUpdatePrompt(false);
    setRemindMeCount(newCount);
  };

  return (
    <div className="app-container">
      <div className={isDesktop ? styles.pageBuilding : "mobile-app-container"}>
        <div className={isDesktop ? styles.pageLayout : "mobile-page-layout"}>
          <NewVersionPopup
            open={showUpdatePrompt}
            onRefresh={handleRefresh}
            onCancel={handleCancel}
            isCancelDisabled={isCancelDisabled}
          />
          <RouteTracker />

          {/* DrawerScreen will handle device-specific drawer rendering */}
          <DrawerScreen />

          {/* Main Content Area */}
          <div
            style={{
              marginTop: isDesktop ? "-" + spacing.Spacing.xs.$value : spacing.Spacing.none.$value,
              width: "100%",
              paddingLeft: isDesktop ? spacing.Spacing.sm.$value : spacing.Spacing.none.$value,
              overflow: "hidden",
              flex: 1,
            }}
          >
            <div style={{ display: "block", width: "100%" }}>
              <ErrorMessageScreen
                message={messageForSnackBar?.message}
                type={messageForSnackBar?.type}
              />
            </div>

            {/* Breadcrumbs - Only show on desktop */}
            {isDesktop && (
              <div className="bread-crumbs" style={{ marginBottom: spacing.Radius.lg.$value }}>
                <BreadCrumbsScreen />
              </div>
            )} 

            <SharedStateProvider>
              <Outlet />
            </SharedStateProvider>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WorkspaceLayout;

